<template>
  <div class="user-info">
    <themed-form
      v-if="users.selectedUser && typeof users.selectedUser === 'object' && !Array.isArray(users.selectedUser)"
      :fields="formFields"
      :model-value="users.selectedUser"
      :errors="errors"
      layout="grid"
      :columns="2"
      :loading="loading"
      @input="handleFormInput"
      @submit="save"
    >
      <!-- 2FA Status Section -->
      <template #after-fields>
        <div
          v-if="($gate.isAdmin() || $gate.isClientAdmin()) && users.selectedUser.id !== 'new'"
          class="user-info__2fa"
        >
          <div class="user-info__2fa-header">
            <div class="user-info__2fa-title">
              <theme-icon icon="lock" type="icons" classes="w-5 h-5" />
              <span>Two-Factor Authentication Status</span>
            </div>
            <themed-badge :variant="get2FABadgeVariant()">
              {{ users.selectedUser.two_fa_status }}
            </themed-badge>
          </div>
          <p class="user-info__2fa-description">
            {{ get2FADescription() }}
          </p>
        </div>

        <!-- Actions -->
        <div v-if="users.selectedUser.id !== 'new'" class="user-info__actions">
          <themed-button
            @click="resetPassword = true"
            variant="secondary"
            type="button"
          >
            Reset Password
          </themed-button>

          <themed-button
            v-if="$gate.isAdmin() || $gate.isClientAdmin()"
            @click="toggle2FA"
            :variant="users.selectedUser.is_2fa_enabled ? 'destructive' : 'default'"
            :disabled="toggling2FA"
            type="button"
          >
            <theme-icon v-if="toggling2FA" icon="loading" type="icons" classes="w-4 h-4 animate-spin" />
            <span v-else>
              {{ users.selectedUser.is_2fa_enabled ? 'Disable 2FA' : 'Enable 2FA' }}
            </span>
          </themed-button>

          <themed-button
            v-if="$gate.isAdmin() || $gate.isClientAdmin()"
            @click="confirmDeleteUser"
            variant="destructive"
            type="button"
          >
            Delete User
          </themed-button>
        </div>
      </template>
    </themed-form>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedBadge from '../ThemedBadge.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'UserInfo',

  components: {
    ThemedForm,
    ThemedButton,
    ThemedBadge,
    ThemeIcon
  },

  data() {
    return {
      roles: [],
      cellCarriers: [],
      countries: [],
      resetPassword: false,
      loading: false,
      errors: {},
      toggling2FA: false
    };
  },

  computed: {
    ...mapState(['users']),

    roleOptions() {
      return this.roles.map(role => ({
        value: role.id,
        label: role.label
      }));
    },

    cellCarrierOptions() {
      return this.cellCarriers.map(carrier => ({
        value: carrier.id,
        label: carrier.carrier
      }));
    },

    countryOptions() {
      return this.countries.map(country => ({
        value: country.id,
        label: country.name
      }));
    },

    formFields() {
      const fields = [
        // Row 1: Name (1 col) + Email (1 col)
        {
          name: 'name',
          type: 'input',
          inputType: 'text',
          label: 'Name',
          required: true,
          col: 1
        },
        {
          name: 'email',
          type: 'input',
          inputType: 'email',
          label: 'Email',
          icon: 'mail',
          required: true,
          col: 1
        },
        // Row 2: Role (1 col) + Department (1 col)
        {
          name: 'role_id',
          type: 'select',
          label: 'Role',
          options: this.roleOptions,
          placeholder: 'Select Role',
          required: true,
          col: 1
        },
        {
          name: 'department',
          type: 'input',
          inputType: 'text',
          label: 'Department',
          componentProps: {
            maxlength: 100
          },
          col: 1
        },
        // Row 3: Title (full width)
        {
          name: 'title',
          type: 'input',
          inputType: 'text',
          label: 'Job Title',
          col: 2
        }
      ];

      // Add password fields conditionally (1 col each)
      if (this.resetPassword || this.users.selectedUser.id === 'new') {
        fields.push(
          {
            name: 'password',
            type: 'input',
            inputType: 'password',
            label: 'Password',
            icon: 'lock',
            required: true,
            col: 1
          },
          {
            name: 'password_confirmation',
            type: 'input',
            inputType: 'password',
            label: 'Confirm Password',
            icon: 'lock',
            required: true,
            col: 1
          }
        );
      }

      // Row 4: Phone (1 col) + Extension (1 col)
      fields.push(
        {
          name: 'phone',
          type: 'input',
          inputType: 'tel',
          label: 'Phone',
          icon: 'phone',
          componentProps: {
            maxlength: 15
          },
          col: 1
        },
        {
          name: 'extension',
          type: 'input',
          inputType: 'text',
          label: 'Phone Extension',
          componentProps: {
            maxlength: 5,
            pattern: '[0-9]*',
            inputmode: 'numeric'
          },
          col: 1
        },
        // Row 5: Cell (1 col) + Cell Carrier (1 col)
        {
          name: 'cell',
          type: 'input',
          inputType: 'tel',
          label: 'Cell',
          icon: 'phone',
          componentProps: {
            maxlength: 15
          },
          col: 1
        },
        {
          name: 'cell_carrier_id',
          type: 'select',
          label: 'Cell Carrier',
          options: this.cellCarrierOptions,
          placeholder: 'Select Carrier',
          col: 1
        },
        // Row 6: Country (full width)
        {
          name: 'country_id',
          type: 'select',
          label: 'Country',
          options: this.countryOptions,
          placeholder: 'Select Country',
          required: true,
          col: 2
        }
      );

      return fields;
    }
  },

  watch: {
    'users.selectedUser.id': {
      handler() {
        this.resetPassword = false;
      }
    },
    
    'users.selectedUser': {
      handler(newUser) {
        // Reload data if options are empty (component reused)
        if (newUser && (!this.roles.length || !this.countries.length)) {
          this.loadData();
        }
      }
    }
  },

  created() {
    this.loadData();
    
    // Listen for save event from parent
    this.$eventHub.$on('save-user-user-info', this.save);
    
    // Listen for modal opened event to reload data
    this.$eventHub.$on('modal-opened-manage-users', this.loadData);
  },

  beforeDestroy() {
    this.$eventHub.$off('save-user-user-info', this.save);
    this.$eventHub.$off('modal-opened-manage-users', this.loadData);
  },

  methods: {
    async loadData() {
      try {
        const [countriesRes, rolesRes, carriersRes] = await Promise.all([
          axios.get('/api/countries'),
          axios.get('/api/roles'),
          axios.get('/api/cell-carriers')
        ]);

        this.countries = countriesRes.data;
        this.roles = rolesRes.data;
        this.cellCarriers = carriersRes.data;
      } catch (error) {
        console.error('Error loading data:', error);
      }
    },

    handleFormInput(updatedData) {
      // Update the user object with the new data
      Object.assign(this.users.selectedUser, updatedData);
    },

    removeUser() {
      this.users.all = _.remove(this.users.all, (user) => {
        return user.id === this.users.selectedUser.id;
      });

      this.users.selectedUser.id = null;
    },

    confirmDeleteUser() {
      if (this.users.selectedUser.id === 'new') {
        this.removeUser();
        return;
      }

      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to delete this User?',
        type: 'warning',
        buttonsStyling: false,
        confirmButtonText: 'Yes - Delete it',
        confirmButtonClass: 'btn btn-red',
        showCancelButton: true,
        cancelButtonClass: 'btn btn-secondary mr-4',
        reverseButtons: true
      }).then((result) => {
        if (result.value) {
          this.deleteUser();
        }
      });
    },

    async deleteUser() {
      try {
        const response = await axios.delete('/users/' + this.users.selectedUser.id);
        
        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message
        });

        // Emit event to notify parent that user was deleted
        this.$eventHub.$emit('user-deleted', this.users.selectedUser.id);
        
        // Close the modal after successful delete
        this.$modal.hide('manage-users');
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete user'
        });
      }
    },

    async save() {
      this.loading = true;
      this.errors = {};

      const method = this.users.selectedUser.id === 'new' ? 'post' : 'put';
      const url = method === 'post' 
        ? '/users' 
        : `/users/${this.users.selectedUser.id}`;

      try {
        const response = await axios[method](url, this.users.selectedUser);
        
        _.merge(this.users.selectedUser, response.data.user);

        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message
        });

        this.$eventHub.$emit('user-saved');
      } catch (error) {
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        }

        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'An error occurred while processing your request.'
        });
        
        this.$eventHub.$emit('user-save-error');
      } finally {
        this.loading = false;
      }
    },

    async toggle2FA() {
      this.toggling2FA = true;
      const formData = new FormData();
      formData.append('enable', !this.users.selectedUser.is_2fa_enabled ? 1 : 0);

      try {
        const response = await axios.post(
          '/users/' + this.users.selectedUser.id + '/toggle-2fa',
          formData
        );

        this.users.selectedUser.is_2fa_enabled = response.data.user.is_2fa_enabled;
        this.users.selectedUser.two_fa_method = response.data.user.two_fa_method;
        this.users.selectedUser.two_fa_status = response.data.user.two_fa_status;

        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message
        });
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to update 2FA status. Please try again.'
        });
      } finally {
        this.toggling2FA = false;
      }
    },

    get2FABadgeVariant() {
      if (this.users.selectedUser.is_2fa_enabled && this.users.selectedUser.two_fa_method) {
        return 'default';
      } else if (this.users.selectedUser.is_2fa_enabled && !this.users.selectedUser.two_fa_method) {
        return 'secondary';
      }
      return 'destructive';
    },

    get2FADescription() {
      if (this.users.selectedUser.is_2fa_enabled && this.users.selectedUser.two_fa_method) {
        return `User has 2FA enabled using ${this.users.selectedUser.two_fa_method}. They will be required to complete 2FA verification on login.`;
      } else if (this.users.selectedUser.is_2fa_enabled && !this.users.selectedUser.two_fa_method) {
        return 'User has 2FA enabled but not yet configured. They will need to complete 2FA setup on their next login.';
      }
      return 'User has 2FA disabled. They can login without 2FA verification.';
    }
  }
};
</script>
